import { useRef, type MouseEvent, type ReactNode } from 'react'
import * as Sentry from '@sentry/electron/renderer'
import { twMerge } from 'tailwind-merge'
import { Button, Popover, Tooltip } from '@highlight/ui'
import type { CalendarEvent } from '@highlight/common'
import { CalendarEventIconView } from '@renderer/components/Icons/CalendarEventIcon'
import type { Calendar } from '@renderer/hooks/use-calendars'
import { FadingScrollable } from '@renderer/chat'
import { useSubmitMessage } from '@renderer/chat/hooks'
import { Skeleton } from '@renderer/components/ui/skeleton'
import { CalendarDayView } from '@renderer/components/calendar-day-view'
import { useDayCalendarEvents } from '@renderer/components/calendar-day-view/use-day-calendar-events'
import { formatTimeRange, formatTimeUntil } from '../../utils/activity-helpers'
import { useMeetingBrief } from './brief/use-meeting-brief'
import { useTriggerBriefGeneration } from './brief/use-trigger-brief-generation'
import { useMeetingBriefGenerationEnabled } from './brief/use-meeting-brief-generation-enabled'
import { useMeetingHoursExhausted, MEETING_HOURS_EXHAUSTED_TOOLTIP } from './brief/use-meeting-hours-exhausted'
import { useMeetingBriefAllowance } from '../../views/meeting-view/nested/brief-generation'
import { ReadingOrangeLoadstone } from '../../views/meeting-view/nested/preparing-indicator'
import { BriefSummarySection } from './brief/sections/brief-summary-section'
import { BriefLinksSection } from './brief/sections/brief-links-section'
import { useMeetingCallsToAction } from './use-meeting-calls-to-action'
import { useMeetingPopoverScheduleShown } from './atoms'
import { AiCardPrompt } from '../ai-card-prompt'
import { useFocusWhenPreviewOpens } from '../todo-popover'

const POPOVER_TRIGGER = 'sidepanel-meeting-popover'

interface MeetingPopoverProps {
  meeting: CalendarEvent
  calendars: Array<Calendar> | undefined
  /** Drills into the Meeting View. The row owns that routing (brief vs plain). */
  onOpenMeeting: (event: MouseEvent) => void
  /** Opens the Meeting View on a chat about it, after a prompt from the brief went out. */
  onOpenMeetingChat: (conversationId: string) => void
  onClose: () => void
  open: boolean
  /** Open without focus, from `useTodoPopover`. */
  previewing: boolean
  /** Swapping with another row's, from `useTodoPopover`, so it neither fades in nor out. */
  swapping: boolean
  /**
   * The row behind this popup is inside its join window, so it hosts the same
   * prominent CTA in place of its times. The header yields to it rather than
   * printing the identical button a few pixels below.
   */
  rowHostsCallToAction: boolean
}

/**
 * The popup is a portaled `Popover.Popup`, which registers its own box with the
 * overlay's hit-test, so any part that overhangs the panel still catches clicks
 * instead of falling through to the desktop. Collision avoidance keeps it on screen.
 */
export function MeetingPopover(props: MeetingPopoverProps) {
  // A developer setting, from Developers > Sidepanel.
  const scheduleShown = useMeetingPopoverScheduleShown()
  const detailsRef = useRef<HTMLDivElement>(null)
  useFocusWhenPreviewOpens(props.open, props.previewing, detailsRef)
  return (
    <Popover.Content
      side="bottom"
      // Pinned at the row's right edge, so the schedule widens it to the left.
      align="end"
      sideOffset={4}
      initialFocus={props.previewing ? false : undefined}
      // Focus stays on the row a step went to.
      finalFocus={props.previewing || props.swapping ? false : undefined}
      // `popupSurface` lands at once and fades out; this one fades and scales both ways.
      // A fixed height, so the brief landing doesn't resize the popover and re-flip it.
      className={twMerge(
        'flex h-[min(400px,var(--available-height))] w-[400px] max-w-[var(--available-width)] origin-[var(--transform-origin)] overflow-hidden p-0 transition-[opacity,transform] data-[ending-style]:scale-[0.97] data-[starting-style]:scale-[0.97] data-[starting-style]:opacity-0 motion-reduce:transition-none',
        scheduleShown && 'w-[600px]',
        props.swapping && 'transition-none',
      )}
    >
      {/* No right padding: the brief column's own padding is the gap between them.
          8px in from the popover's 20px corner, so the card's 12px one is concentric. */}
      {scheduleShown && (
        <div className="min-w-0 flex-1 py-2 pl-2">
          <div className="corner-smooth bg-background-raised h-full rounded-lg border p-1">
            <MeetingPopoverSchedule meeting={props.meeting} />
          </div>
        </div>
      )}
      <div
        ref={detailsRef}
        tabIndex={-1}
        className="flex min-h-0 w-[400px] shrink-0 flex-col gap-4 p-3 focus-visible:outline-none"
      >
        <MeetingPopoverHeader {...props} />
        <MeetingPopoverBrief meeting={props.meeting} onOpenMeetingChat={props.onOpenMeetingChat} />
      </div>
    </Popover.Content>
  )
}

/**
 * The CTA subscriptions mount here, not on the row. The portal renders nothing
 * while the popover is closed.
 */
function MeetingPopoverHeader({
  meeting,
  calendars,
  onOpenMeeting,
  onClose,
  rowHostsCallToAction,
}: MeetingPopoverProps) {
  const { primary, viewSummary } = useMeetingCallsToAction(meeting, POPOVER_TRIGGER)
  // The row only swaps its times for the CTA while that CTA is prominent, so
  // that is the one case the two buttons are the same button.
  const showPrimary = primary && !(rowHostsCallToAction && primary.isProminent)
  const closeThen = (select: (event: MouseEvent) => void) => (event: MouseEvent) => {
    onClose()
    select(event)
  }
  const start = new Date(meeting.start)
  const end = meeting.end ? new Date(meeting.end) : undefined

  return (
    <div className="flex shrink-0 items-start gap-3">
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <Popover.Title className="line-clamp-2 text-lg font-semibold">{meeting.title}</Popover.Title>
        <div className="text-foreground-secondary type-ui-sm flex min-w-0 items-center gap-1.5 tabular-nums">
          <CalendarEventIconView event={meeting} size={14} calendars={calendars} />
          <span className="truncate">
            {formatTimeRange(start, end)} · {formatTimeUntil(start, end)}
          </span>
        </div>
      </div>
      {/* One line of the `text-lg` title, so the buttons stay on its first line when it wraps. */}
      <div className="flex h-[26px] shrink-0 items-center gap-1.5">
        {viewSummary && (
          <Button size="sm" variant="secondary" onClick={closeThen(viewSummary)}>
            View Summary
          </Button>
        )}
        <Button size="sm" variant="secondary" onClick={onOpenMeeting}>
          Open
        </Button>
        {showPrimary && (
          <Button
            size="sm"
            variant={primary.isEarly ? 'secondary' : 'primary'}
            tone={primary.isProminent ? 'inverse' : 'neutral'}
            loading={primary.isPending}
            onClick={closeThen(primary.onSelect)}
          >
            {primary.label}
          </Button>
        )}
      </div>
    </div>
  )
}

/**
 * Shares the Meeting View's brief query, so a brief fetched here is already
 * there when Open drills in. A ready brief fills the card, which scrolls under
 * the prompt held at its foot.
 */
function MeetingPopoverBrief({
  meeting,
  onOpenMeetingChat,
}: Pick<MeetingPopoverProps, 'meeting' | 'onOpenMeetingChat'>) {
  const submitMessage = useSubmitMessage()
  // Calendar GlobalState carries only the brief's presence. The body is fetched.
  const hasBriefMeta = !!meeting.brief?.conversationId
  const {
    data: fetchedBrief,
    isLoading,
    isGenerating,
  } = useMeetingBrief(meeting.id, {
    pollWhilePending: hasBriefMeta || !!meeting.briefGenerating,
    presenceConversationId: meeting.brief?.conversationId,
  })
  const legacyInlineBrief = meeting.brief?.brief?.trim() ? meeting.brief : null
  const brief = fetchedBrief ?? legacyInlineBrief
  const briefConversationId = fetchedBrief?.conversationId

  if (brief) {
    // Follow-ups go to the brief's own conversation, as they do from the Meeting View's composer.
    const askAboutBrief = (prompt: string) => {
      if (!briefConversationId) return
      void submitMessage(prompt, { conversationId: briefConversationId, onSubmit: onOpenMeetingChat }).catch(
        (error: unknown) => Sentry.captureException(error),
      )
    }
    return (
      <BriefCard className="min-h-0 flex-1">
        <FadingScrollable className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto p-3">
          <BriefSummarySection
            markdown={brief.brief}
            structure={fetchedBrief?.structure}
            previews={fetchedBrief?.linkPreviews}
            size="sm"
          />
          <BriefLinksSection links={brief.relevantLinks} previews={fetchedBrief?.linkPreviews} />
        </FadingScrollable>
        {briefConversationId && (
          <AiCardPrompt placeholder="Ask about this meeting…" onSubmit={askAboutBrief} className="m-1.5 mt-0" />
        )}
      </BriefCard>
    )
  }
  if (meeting.briefGenerating || isGenerating) {
    return (
      <BriefCard className="p-3">
        <BriefSkeleton label="Generating brief" />
      </BriefCard>
    )
  }
  // Only a meeting known to have a brief gets a skeleton. Without one, the
  // fetch almost always comes back empty, so the empty line waits for it.
  if (isLoading) {
    return hasBriefMeta ? (
      <BriefCard className="p-3">
        <BriefSkeleton />
      </BriefCard>
    ) : null
  }
  return (
    <BriefCard className="gap-3 p-3">
      <GenerateBriefButton key={meeting.id} meeting={meeting} />
    </BriefCard>
  )
}

function GenerateBriefButton({ meeting }: { meeting: CalendarEvent }) {
  const generationEnabled = useMeetingBriefGenerationEnabled()
  const outOfHours = useMeetingHoursExhausted()
  const briefs = useMeetingBriefAllowance()
  const trigger = useTriggerBriefGeneration()
  const hasEnded = !!meeting.end && new Date(meeting.end).getTime() < Date.now()

  if (!generationEnabled || !meeting.id || hasEnded) {
    return <p className="text-foreground-secondary type-ui-sm">No brief for this meeting.</p>
  }

  const outOfBriefs = briefs != null && briefs.remaining <= 0
  const disabledReason = outOfHours
    ? MEETING_HOURS_EXHAUSTED_TOOLTIP
    : outOfBriefs
      ? 'Your meeting briefs for this billing period are used up.'
      : undefined

  return (
    <div className="flex flex-col items-start gap-2">
      <p className="text-foreground type-ui-sm font-medium">Want Highlight to help you prepare?</p>
      <p className="text-foreground-secondary type-ui-sm">
        Get a brief with useful context and talking points for this meeting.
      </p>
      {briefs && (
        <p className="text-foreground-secondary type-ui-xs">
          {briefs.remaining} of {briefs.limit} briefs left this billing period
        </p>
      )}
      <Tooltip disabled={!disabledReason}>
        <Tooltip.Trigger render={<div className="flex" />}>
          <Button
            size="sm"
            variant="primary"
            tone="inverse"
            loading={trigger.isPending}
            disabled={briefs === undefined || !!disabledReason}
            onClick={() => trigger.mutate(meeting.id, { onError: (error) => Sentry.captureException(error) })}
          >
            {trigger.isPending ? 'Generating brief' : 'Generate brief'}
          </Button>
        </Tooltip.Trigger>
        <Tooltip.Content>{disabledReason}</Tooltip.Content>
      </Tooltip>
      {trigger.isError && (
        <p role="alert" className="text-danger-subtle type-ui-sm">
          Could not generate the brief. Please try again.
        </p>
      )}
    </div>
  )
}

function BriefCard({ className, children }: { className?: string; children: ReactNode }) {
  return (
    <div className={twMerge('corner-smooth bg-element flex shrink-0 flex-col overflow-hidden rounded-xl', className)}>
      {children}
    </div>
  )
}

/** Today's synced events, with this meeting picked out on its day. */
function MeetingPopoverSchedule({ meeting }: { meeting: CalendarEvent }) {
  const { events } = useDayCalendarEvents()
  return (
    <CalendarDayView
      compact
      day={new Date(meeting.start)}
      events={events}
      selectedEventId={meeting.id}
      now={new Date()}
      className="m-0 h-full bg-transparent p-0 shadow-none"
    />
  )
}

const BULLET_WIDTHS = ['w-[92%]', 'w-[78%]', 'w-[85%]']

/** A headline and three bullets, the shape a brief lands in, so the swap barely moves. */
function BriefSkeleton({ label }: { label?: string }) {
  return (
    <div className="flex flex-col gap-3" role="status" aria-label={label ?? 'Loading brief'}>
      <div aria-hidden="true" className="flex flex-col gap-2">
        <Skeleton className="h-3 w-full" />
        <Skeleton className="h-3 w-[64%]" />
      </div>
      <div aria-hidden="true" className="flex flex-col gap-2.5 pl-1">
        {BULLET_WIDTHS.map((width) => (
          <div key={width} className="flex items-center gap-2">
            <Skeleton className="size-1 shrink-0 rounded-full" />
            <Skeleton className={`h-3 ${width}`} />
          </div>
        ))}
      </div>
      {label && (
        <p className="text-foreground-secondary type-ui-xs flex items-center gap-1.5">
          <ReadingOrangeLoadstone size={12} />
          {label}
        </p>
      )}
    </div>
  )
}
